import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { useQueryClient } from '@tanstack/react-query'
import { Search } from 'lucide-react'
import { PageLayout } from '@/components/layout/PageLayout'
import { PageHeader } from '@/components/layout/PageHeader'
import { Input } from '@/components/shadcn/input'
import {
  getGetApiCompaniesCurrentShopmodulesQueryKey,
  useGetApiCompaniesCurrentGroups,
  useGetApiCompaniesCurrentShopmodules,
  useGetApiCompaniesWalletTypes,
} from '@/api/generated/company-management-user/company-management-user'
import { shopModuleLabel } from '@/pages/groups/components/ShopModuleIcon'
import { ShopModuleGrid } from './grid-components/ShopModuleGrid'
import { EditShopModuleDialog } from './components/EditShopModuleDialog'
import { filterShopModules, sortShopModules } from './lib'
import type { BaseShopModuleModel } from '@/api/model'

/**
 * Overview of the company's shop modules. They are created and deleted by Bonisoft, not in
 * the portal — there are no create/delete endpoints for company managers. The only thing a
 * manager can change is the wallet type of a module (details:
 * `docs/plans/shop-modules-page.md`).
 */
export default function ShopModulesPage() {
  // `groups` is needed because shopModuleLabel() reads its type labels from that NS.
  const { t, i18n } = useTranslation(['shop-modules', 'groups', 'filters'])
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const [editingModule, setEditingModule] = useState<BaseShopModuleModel | null>(null)

  const { data, isLoading, isError } = useGetApiCompaniesCurrentShopmodules()
  // Both lookups only turn ids into names on the cards — a failure degrades a single
  // cell, so they deliberately don't feed the page's loading/error state.
  const { data: walletTypesData } = useGetApiCompaniesWalletTypes()
  const { data: groupsData } = useGetApiCompaniesCurrentGroups()

  const walletTypes = walletTypesData?.data ?? []
  const groups = groupsData?.data ?? []

  const visibleModules = useMemo(() => {
    const modules = data?.data ?? []
    // i18n.resolvedLanguage is not read here, but it keeps the memo honest: the sort and
    // the search both run on translated labels, so a language switch must recompute.
    void i18n.resolvedLanguage
    return filterShopModules(sortShopModules(modules, shopModuleLabel), search, shopModuleLabel)
  }, [data, search, i18n.resolvedLanguage])

  return (
    <PageLayout scrollEnabled>
      <PageHeader title={t('header.title')} description={t('header.description')} />

      <div className="relative w-64">
        <Search className="absolute left-2.5 top-2.5 size-4 text-muted-foreground" />
        <Input
          placeholder={t('filters:searchbar.searchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-8 h-9"
        />
      </div>

      <ShopModuleGrid
        modules={visibleModules}
        walletTypes={walletTypes}
        groups={groups}
        isLoading={isLoading}
        isError={isError}
        isFiltered={!!search.trim()}
        // Only FullPayout modules reach this — the grid decides per type.
        onOpen={(module) => navigate(`/shop-modules/${module.id}`)}
        onEdit={setEditingModule}
      />

      {editingModule && (
        <EditShopModuleDialog
          module={editingModule}
          onClose={() => setEditingModule(null)}
          onSaved={() => queryClient.invalidateQueries({
            queryKey: getGetApiCompaniesCurrentShopmodulesQueryKey(),
          })}
        />
      )}
    </PageLayout>
  )
}
